import { memo, ReactNode, useCallback, useEffect, useState } from "react";

import {
  ArrowsTriangle as SoulboundIcon,
  BracketsEllipses as ContractIcon,
  ClockNoFill as PriceDropIntervalIcon,
  CoinsFill as PriceIcon,
  CoinsHand as LimitMintsIcon,
  CoinsStacked as MaxMintsIcon,
  Cube as BlockchainIcon,
  ErrorTriangle,
  LayersThree as SizeIcon,
  LetterSpacing as NameIcon,
  Logo as CollectionLogoIcon,
  ParagraphBlack as DescriptionIcon,
  Percent as RoyaltyIcon,
  PlayCircle as MintStartIcon,
  RocketWhite as DeployIcon,
  Shield as OpenseaBlocklistIcon,
  StopCircle as MintEndIcon,
  UnlockedGate as GateIcon,
  User as PayoutPersonIcon,
  Gift as SponsoredIcon,
} from "@hl/base-components/lib/assets/icons.generated";
import { STATUS_COLOR, TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import UploadFileProgress from "@hl/base-components/lib/upload/UploadFileProgress";
import {
  UploadStatus,
  useUploadManager,
} from "@hl/base-components/lib/upload/context";
import { TransactionType } from "@hl/shared-features/lib/apollo/graphql.generated";
import { useAuth } from "@hl/shared-features/lib/features/auth/AuthContext";
import {
  FEATURE_FLAGS,
  useFeatureFlags,
} from "@hl/shared-features/lib/features/auth/hooks/useFeatureFlags";
import {
  TransactionStateType,
  useTransactionsDispatch,
  useTransactionState,
} from "@hl/shared-features/lib/features/evm-tx/TransactionContext";
import {
  AppNotification,
  AppEventType,
  HLServiceEventType,
} from "@hl/shared-features/lib/features/websocket/events";
import useEventSubscription from "@hl/shared-features/lib/features/websocket/useEventSubscription";
import { logError } from "@hl/shared-features/lib/services/logger";
import { networkLookup } from "@hl/shared-features/lib/utils/blockExplorer";
import { maskAddress } from "@hl/shared-features/lib/utils/content";
import {
  Anchor,
  Box,
  Button,
  Divider,
  Group,
  Space,
  Stack,
  Text,
  UnstyledButton,
  useMantineTheme,
} from "@mantine/core";
import ReactMarkdown from "react-markdown";
import { ReactElement } from "react-markdown/lib/react-markdown";
import { useNavigate } from "react-router-dom";
import gfm from "remark-gfm";

import {
  _CollectionType,
  Contract2,
  DraftProgress,
  PriceType,
  SupportedMediaType,
  TokenDistributionType,
  GenerativeEditionType,
} from "apollo/graphql.generated";
import { ReactComponent as EyeOffIcon } from "assets/icons/eye-off.svg";
import { ReactComponent as Star6Icon } from "assets/icons/star-06.svg";
import { TOOLS_URL } from "config";
import { shortFormatDate } from "utils/date";
import FormContentWithPreview from "~/features/collections/forms/CreateCollectionMultistepForm/FormContentWithPreview";
import UnzipProgress from "~/features/collections/forms/CreateCollectionMultistepForm/UnzipProgress";
import {
  getStepByPath,
  useCreateCollectionFormContext,
  useCreateCollectionState,
} from "~/features/collections/forms/CreateCollectionMultistepForm/context";
import { getPreviewImageUrl } from "~/features/collections/forms/CreateCollectionMultistepForm/previewUtil";
import useCurrencyDisplay from "~/features/collections/forms/CreateCollectionMultistepForm/useCurrencyDisplay";
import { draftFormToEntity } from "~/features/collections/forms/CreateCollectionMultistepForm/useSaveDraft";
import CollectionDeployModal from "~/features/collections/modals/CollectionDeployModal";
import { useGateQuery } from "~/features/gates/queries/gates.graphql.generated";
import FeatureNotAvailable from "~/features/layout/FeatureNotAvailable";

import {
  useCreateCollectionDraftMutation,
  useDeployCollectionParamsQuery,
  useDeployDraftMutation,
  useUpdateCollectionDraftMutation,
} from "../../queries/draft.graphql.generated";

import { DeployFinishTracking } from "./DeployFinishTracking";
import FormTitle from "./FormTitle";

export type DetailSectionLineProps = {
  leftIcon: ReactNode;
  label: string;
  value?: ReactElement | string | null;
  description?: ReactNode | null;
  link?: string;
  isLoading?: boolean;
};

const JumpStepButton = ({
  children,
  step,
}: {
  children: ReactNode;
  step: number;
}) => {
  const { dispatch } = useCreateCollectionState();
  const form = useCreateCollectionFormContext();
  const theme = useMantineTheme();

  return (
    <UnstyledButton
      onClick={() => {
        dispatch({
          type: "JUMP_TO_STEP",
          payload: { currentStep: step, form },
        });
      }}
    >
      <Group spacing={8}>
        <ErrorTriangle color={theme.colors.errorStatus[0]} />
        <Text size="sm" color={STATUS_COLOR.ERROR}>
          {children}
        </Text>
      </Group>
    </UnstyledButton>
  );
};

const DetailSectionLine = ({
  leftIcon,
  label,
  description,
  link,
  isLoading = false,
  value,
}: DetailSectionLineProps) => {
  const Content = () => (
    <>
      <Space h={4} />
      <Group position="apart" noWrap>
        <Group spacing={10} noWrap>
          <Box miw={20}>{leftIcon}</Box>
          <Text size="sm">{label}</Text>
        </Group>
        <Text
          size="sm"
          color={TEXT_COLOR.SECONDARY}
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {description}
          {isLoading && !description && "Loading..."}
        </Text>
      </Group>
      {value && <Text mt="md">{value}</Text>}
    </>
  );
  return (
    <>
      {link ? (
        <Anchor target="_blank" rel="noreferrer" href={link} variant="text">
          <Content />
        </Anchor>
      ) : (
        <Content />
      )}

      <Divider my="xs" />
    </>
  );
};

interface ReviewAndDeployProps {
  handleValidate?: () => boolean;
  handleSubmit: () => void;
  contracts?: Contract2[];
  buttonOnly?: boolean;
}

const ReviewAndDeploy = ({
  handleValidate,
  handleSubmit,
  contracts,
  buttonOnly = false,
}: ReviewAndDeployProps) => {
  const {
    state: { draftId },
  } = useCreateCollectionState();
  const navigate = useNavigate();
  const form = useCreateCollectionFormContext();
  const {
    moduleSettings,
    gateId,
    metadata,
    collection,
    collectionType,
    distribution,
    contract,
    generativeDetails,
    withoutMintVector,
  } = form.values;

  const { displayCurrency } = useCurrencyDisplay();

  const {
    state: { status },
  } = useUploadManager();
  const { data } = useGateQuery({
    variables: {
      id: gateId!,
    },
    skip: !gateId,
  });

  const getNewTxnId = () => `txn-${Date.now()}`;
  const [txnId] = useState<string>(getNewTxnId());
  const [showDescription, setShowDescription] = useState<boolean>(false);

  // const transaction = useTransaction()
  const transactionState = useTransactionState(txnId);
  const transactionsDispatch = useTransactionsDispatch();

  const { walletAddress } = useAuth();

  const { dispatch, state } = useCreateCollectionState();
  const { draftProgress } = state;

  const [createCollectionDraftMutation, { loading: createDraftLoading }] =
    useCreateCollectionDraftMutation();

  const [updateCollectionDraftMutation, { loading: updateDraftLoading }] =
    useUpdateCollectionDraftMutation();

  const { data: deployData } = useDeployCollectionParamsQuery({
    variables: { draftId: state.draftId ?? "" },
    skip: draftProgress !== DraftProgress.AWAITING_SIGNATURE || !state.draftId,
  });

  const onDraftUpdate = async (draftId: string) => {
    console.log("on draft update", draftId, form.values);
    const response = await updateCollectionDraftMutation({
      variables: {
        draftId,
        updateCollectionDraftData: draftFormToEntity(
          form.values,
          DraftProgress.ADDED_DISTRIBUTION,
          walletAddress,
          true
        ),
      },
    });
    if (response.errors) {
      setGeneralError(
        "Failed to update draft. Please try again or contact support."
      );
      logError(
        { e: response.errors },
        `Failed to update a draft. draftId: ${draftId}`
      );
    }
  };

  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const handleCollectionUpdate = (message: any) => {
    switch (message.type) {
      case HLServiceEventType.CollectionDeployReady:
        dispatch({
          type: "UPDATE_DRAFT_PROGRESS",
          payload: {
            draftProgress: DraftProgress.AWAITING_SIGNATURE,
          },
        });
        break;
      case HLServiceEventType.CollectionDeployFailed:
        transactionsDispatch?.({
          type: "UPDATE_TX_STATE",
          payload: {
            id: txnId,
            type: TransactionStateType.Web2UpdateError,
          },
        });
        break;
    }
  };

  useEventSubscription(
    AppEventType.CreateCollectionUpdate,
    (message: AppNotification) => {
      console.log("APP NOTIFICATION", { message });
      handleCollectionUpdate(JSON.parse(message.payload));
    }
  );

  const deployModal =
    state &&
    state.draftId &&
    state.draftProgress &&
    [
      DraftProgress.INITIATED_DEPLOY,
      DraftProgress.AWAITING_SIGNATURE,
      DraftProgress.DEPLOY_FAILED,
    ].includes(state.draftProgress) ? (
      <CollectionDeployModal
        txnId={txnId}
        open={true}
        manageCollectionHandler={() => {
          navigateToManagePage();
        }}
        onRetryUpload={() => {
          transactionsDispatch?.({
            type: "UPDATE_TX_STATE",
            payload: {
              id: txnId,
              type: TransactionStateType.WaitingWeb2Update,
            },
          });
          createDraft(true);
        }}
        onRetryTransaction={() => {
          transactionsDispatch?.({
            type: "UPDATE_TX_STATE",
            payload: {
              id: txnId,
              type: TransactionStateType.WaitingPrepareTx,
            },
          });
        }}
      />
    ) : (
      <></>
    );

  const upsertDraft = async () => {
    if (draftId) {
      await onDraftUpdate(draftId);
      return draftId;
    } else {
      const draft = await createCollectionDraftMutation({
        variables: {
          createCollectionDraftData: {
            collectionType: form.values.collectionType,
          },
        },
      });
      const draftId = draft.data?.createCollectionDraft.draft.id;
      if (draft.errors || !draftId) {
        setGeneralError(
          "Failed to create draft. Please try again or contact support."
        );
        logError(
          { e: draft.errors },
          `Failed to create a draft. draftId: ${draftId}`
        );
      } else {
        dispatch({
          type: "CREATE_DRAFT",
          payload: {
            draftId,
          },
        });
        await onDraftUpdate(draftId);
        return draftId;
      }
    }
  };

  const [
    deployCollectionDraft,
    { loading: deployCollectionLoading, reset: deployCollectionDraftReset },
  ] = useDeployDraftMutation({
    onCompleted(data) {
      if (data && walletAddress) {
        dispatch({
          type: "UPDATE_DRAFT",
          payload: {
            draftId: data.deployCollection.id,
            draftProgress: data.deployCollection.furthestStep,
            unzipped: data.deployCollection.metadata?.seriesS3Unzipped ?? false,
          },
        });
        handleSubmit();
      }
    },
    onError(err) {
      logError({
        err,
        message:
          "failed to deploy a collection draft, as well as its contract and mint-vector / auction",
      });
      setGeneralError(err.message);
      deployCollectionDraftReset();
    },
  });

  const loading =
    createDraftLoading ||
    updateDraftLoading ||
    deployCollectionLoading ||
    !!transactionState;

  const [generalError, setGeneralError] = useState<string>("");

  // entry point when "submit" is clicked, does validation and kicks off a
  // switchNetwork to make sure we're on the right network before proceeding
  const handleFormSubmit = async () => {
    if (handleValidate && !handleValidate()) {
      return;
    }

    if (collectionType === _CollectionType.Series && !skipValidation) {
      if (status === UploadStatus.IN_PROGRESS) {
        setGeneralError("Please wait for the series file upload to finish");
        return;
      } else if (status === UploadStatus.ERROR) {
        setGeneralError("File upload failed. Please upload again.");
        return;
      }
    }

    await createDraft();
  };

  const createDraft = async (isRetry = false) => {
    const draftId = isRetry ? state.draftId : await upsertDraft();

    if (draftId) {
      await deployCollectionDraft({
        variables: {
          draftId,
        },
      });
    }
  };

  useEffect(() => {
    if (
      draftId &&
      draftProgress &&
      [
        DraftProgress.INITIATED_DEPLOY,
        DraftProgress.AWAITING_SIGNATURE,
        DraftProgress.DEPLOY_FAILED,
      ].includes(draftProgress) &&
      !transactionState &&
      state.collectionType
    ) {
      transactionsDispatch?.({
        type: "START_TRANSACTION",
        payload: {
          id: txnId,
          transactionType: TransactionType.EVM_DEPLOY_COLLECTION,
          entityId: draftId,
          collectionType: state.collectionType,
        },
      });
      if (draftProgress === DraftProgress.DEPLOY_FAILED) {
        transactionsDispatch?.({
          type: "UPDATE_TX_STATE",
          payload: {
            id: txnId,
            type: TransactionStateType.Web2UpdateError,
          },
        });
      }
    }
  }, [
    draftId,
    draftProgress,
    transactionState,
    transactionsDispatch,
    state.collectionType,
  ]);

  const navigateToManagePage = useCallback(() => {
    if (deployData)
      navigate(
        `${TOOLS_URL.collections.manage.replace(
          ":collectionId",
          deployData.deployCollectionParams.collection.id
        )}?txnId=${txnId}`
      );
  }, [deployData]);

  useEffect(() => {
    if (transactionState && transactionState.createdEvmTx && deployData) {
      navigateToManagePage();
    }
  }, [transactionState, deployData]);

  useEffect(() => {
    if (deployData && txnId) {
      transactionsDispatch?.({
        type: "UPDATE_TX_ARGS",
        payload: {
          id: txnId,
          args: deployData.deployCollectionParams.transactionArgs,
          metadata: {
            contractId: deployData.deployCollectionParams.contract.id,
            collectionId: deployData.deployCollectionParams.collection.id,
            type: deployData.deployCollectionParams.collection.collectionType,
          },
          contractType: deployData.deployCollectionParams.contract.contractType,
        },
      });
    }
  }, [txnId, deployData, transactionsDispatch]);

  /**
   * Validate form in case user has jumped through the steps.
   */
  useEffect(() => {
    if (!buttonOnly) {
      form.validate();
    }
  }, [buttonOnly]);

  const blockchain = networkLookup(collection.chainId);

  const rows = [
    {
      leftIcon: <BlockchainIcon />,
      label: "Blockchain",
      description: blockchain.displayName,
    },
    {
      leftIcon: <ContractIcon />,
      label: "Contract",
      description:
        form.errors["contract.type"] ||
        form.errors["contract.data.name"] ||
        form.errors["contract.data.symbol"] ? (
          <Stack>
            {form.errors["contract.type"] && (
              <JumpStepButton step={getStepByPath("contract", collectionType)}>
                {form.errors["contract.type"]}
              </JumpStepButton>
            )}
            {form.errors["contract.data.name"] && (
              <JumpStepButton step={getStepByPath("contract", collectionType)}>
                {form.errors["contract.data.name"]}
              </JumpStepButton>
            )}
            {form.errors["contract.data.symbol"] && (
              <JumpStepButton step={getStepByPath("contract", collectionType)}>
                {form.errors["contract.data.symbol"]}
              </JumpStepButton>
            )}
          </Stack>
        ) : contract.type === "new" ? (
          contract.data.name
        ) : (
          contracts?.find(
            (existingContract) =>
              existingContract.id === contract.data.contractId
          )?.name ?? "Contract name"
        ),
    },
    {
      leftIcon: <CollectionLogoIcon />,
      label: "Collection media",
      description: form.errors["metadata.mediaUrl"] ? (
        <JumpStepButton
          step={getStepByPath("metadata.mediaUrl", collectionType)}
        >
          {form.errors["metadata.mediaUrl"]}
        </JumpStepButton>
      ) : (
        "Uploaded"
      ),
    },
    ...(metadata.mediaType !== SupportedMediaType.IMAGE &&
    metadata.mediaType !== "zip"
      ? [
          {
            leftIcon: <CollectionLogoIcon />,
            label: "Collection cover image",
            description: form.errors["metadata.coverMediaUrl"] ? (
              <JumpStepButton
                step={getStepByPath("metadata.coverMediaUrl", collectionType)}
              >
                {form.errors["metadata.coverMediaUrl"]}
              </JumpStepButton>
            ) : (
              "Uploaded"
            ),
          },
        ]
      : []),
    ...([_CollectionType.Series, _CollectionType.GenerativeSeries].includes(
      collectionType
    )
      ? [
          {
            leftIcon: <CollectionLogoIcon />,
            label: "Collection logo image",
            description: form.errors["contract.data.collectionLogoImage"] ? (
              <JumpStepButton
                step={getStepByPath(
                  "contract.data.collectionLogoImage",
                  collectionType
                )}
              >
                {form.errors["contract.data.collectionLogoImage"]}
              </JumpStepButton>
            ) : (
              "Uploaded"
            ),
          },
        ]
      : []),
    {
      leftIcon: <NameIcon />,
      label: "Collection Name",
      description: form.errors["collection.name"] ? (
        <JumpStepButton step={getStepByPath("collection.name", collectionType)}>
          {form.errors["collection.name"]}
        </JumpStepButton>
      ) : (
        collection.name
      ),
    },
    {
      leftIcon: <DescriptionIcon height={20} width={20} />,
      label: "Description",
      description: form.errors["collection.description"] ? (
        <JumpStepButton
          step={getStepByPath("collection.description", collectionType)}
        >
          {form.errors["collection.description"]}
        </JumpStepButton>
      ) : (
        <>
          <Anchor
            size="sm"
            onClick={() => setShowDescription(!showDescription)}
            underline={false}
          >
            <Text size="sm">
              {showDescription ? "Hide description" : "See description"}
            </Text>
          </Anchor>
        </>
      ),
      value: showDescription ? (
        <Text size="sm">
          <ReactMarkdown remarkPlugins={[gfm]}>
            {collection.description ?? ""}
          </ReactMarkdown>
        </Text>
      ) : undefined,
    },
    collectionType === _CollectionType.Series
      ? {
          leftIcon: <SizeIcon />,
          label: "Collection size",
          description: form.errors["metadata.seriesSize"] ? (
            <JumpStepButton
              step={getStepByPath("metadata.seriesSize", collectionType)}
            >
              {form.errors["metadata.seriesSize"]}
            </JumpStepButton>
          ) : (
            `${metadata.seriesSize} items`
          ),
        }
      : {
          leftIcon: <SizeIcon />,
          label:
            (collectionType === _CollectionType.GenerativeSeries
              ? "Series"
              : "Edition") + " size",
          description: form.errors["collection.size"] ? (
            <JumpStepButton
              step={getStepByPath("collection.size", collectionType)}
            >
              {form.errors["collection.size"]}
            </JumpStepButton>
          ) : collectionType === _CollectionType.OneOfOne ? (
            "1 of 1"
          ) : collectionType === _CollectionType.OpenEdition ||
            (collectionType === _CollectionType.GenerativeSeries &&
              generativeDetails?.editionType === GenerativeEditionType.Open) ? (
            "Open"
          ) : (
            `${collection.size} items`
          ),
        },
    /** TODO: https://linear.app/highlight-xyz/issue/ENG-4672/[frontend]-featured-token-field-in-review-and-deploy-step */
    !withoutMintVector &&
      distribution.priceType === PriceType.Fixed && {
        leftIcon: <PriceIcon />,
        label:
          distribution.type === TokenDistributionType.Auction
            ? "Reserve price"
            : "Price",
        description: form.errors["distribution.price"] ? (
          <JumpStepButton
            step={getStepByPath("distribution.price", collectionType)}
          >
            {form.errors["distribution.price"]}
          </JumpStepButton>
        ) : (
          `${distribution.price} ${displayCurrency}`
        ),
      },
    !withoutMintVector &&
      distribution.priceType === PriceType.RankedAuction && {
        leftIcon: <PriceIcon />,
        label: "Minimum bid",
        description: form.errors["distribution.minBid"] ? (
          <JumpStepButton
            step={getStepByPath("distribution.minBid", collectionType)}
          >
            {form.errors["distribution.minBid"]}
          </JumpStepButton>
        ) : (
          `${distribution.minBid} ${displayCurrency}`
        ),
      },
    !withoutMintVector &&
      distribution.priceType === PriceType.DutchAuction && {
        leftIcon: <PriceIcon />,
        label: "Prices",
        description: form.errors["distribution.scheduledPrices"] ? (
          <JumpStepButton
            step={getStepByPath("distribution.scheduledPrices", collectionType)}
          >
            {form.errors["distribution.scheduledPrices"]}
          </JumpStepButton>
        ) : (
          `${distribution.scheduledPrices?.join(" → ")} ${displayCurrency}`
        ),
      },
    !withoutMintVector &&
      distribution.priceType === PriceType.DutchAuction && {
        leftIcon: <PriceDropIntervalIcon />,
        label: "Price drop interval",
        description: form.errors["distribution.priceDropInterval"] ? (
          <JumpStepButton
            step={getStepByPath(
              "distribution.priceDropInterval",
              collectionType
            )}
          >
            {form.errors["distribution.priceDropInterval"]}
          </JumpStepButton>
        ) : (
          distribution.priceDropInterval?.map((period) =>
            period.value > 0
              ? ` ${period.value}${period.unit.charAt(0).toLowerCase()}`
              : ""
          )
        ),
      },
    !withoutMintVector && {
      leftIcon: <MintStartIcon />,
      label: `${
        distribution.type === TokenDistributionType.Auction ? "Auction" : "Mint"
      } start date`,
      description: shortFormatDate(
        distribution.saleDateFrom
          ? new Date(distribution.saleDateFrom)
          : new Date()
      ),
    },
    !withoutMintVector && {
      leftIcon: <MintEndIcon />,
      label: `${
        distribution.type === TokenDistributionType.Auction ? "Auction" : "Mint"
      } end date`,
      description: form.errors["distribution.saleDateTo"] ? (
        <JumpStepButton
          step={getStepByPath("distribution.saleDateTo", collectionType)}
        >
          {form.errors["distribution.saleDateTo"]}
        </JumpStepButton>
      ) : distribution.saleDateTo ? (
        shortFormatDate(new Date(distribution.saleDateTo))
      ) : (
        "None"
      ),
    },
    {
      leftIcon: <GateIcon height={20} width={20} />,
      label: "Gate",
      description: gateId ? data?.gate.name ?? null : "None",
      isLoading: !data?.gate.name,
    },
  ];

  if (collectionType === _CollectionType.Series) {
    rows.push(
      {
        leftIcon: <Star6Icon width={20} height={20} />,
        label: "Mint style",
        description: form.errors["metadata.reveal"] ? (
          <JumpStepButton
            step={getStepByPath("metadata.reveal", collectionType)}
          >
            {form.errors["metadata.reveal"]}
          </JumpStepButton>
        ) : metadata.reveal ? (
          "Random mint"
        ) : (
          "Collector's choice"
        ),
      },
      {
        leftIcon: <EyeOffIcon width={20} height={20} />,
        label: "Hide series metadata",
        description: form.errors["metadata.showAllMetadata"] ? (
          <JumpStepButton
            step={getStepByPath("metadata.showAllMetadata", collectionType)}
          >
            {form.errors["metadata.showAllMetadata"]}
          </JumpStepButton>
        ) : metadata.showAllMetadata ? (
          "No"
        ) : (
          "Yes"
        ),
      }
    );
  }

  if (!!moduleSettings?.customPaymentRecipient?.eoa) {
    rows.push({
      leftIcon: <PayoutPersonIcon />,
      label: `${
        distribution.type === TokenDistributionType.Auction
          ? "Auction"
          : "Primary sales"
      } payout address`,
      description: form.errors[
        "moduleSettings.customPaymentRecipient.address"
      ] ? (
        <JumpStepButton
          step={getStepByPath(
            "moduleSettings.customPaymentRecipient.address",
            collectionType
          )}
        >
          {form.errors["moduleSettings.customPaymentRecipient.address"]}
        </JumpStepButton>
      ) : (
        maskAddress(moduleSettings.customPaymentRecipient.eoa, 6, 4)
      ),
    });
  }
  if (
    moduleSettings.royalty.royaltyAmount ||
    form.errors["moduleSettings.royalty.royaltyAmount"] ||
    form.errors["moduleSettings.royalty.address"]
  ) {
    rows.push(
      {
        leftIcon: <RoyaltyIcon />,
        label: "Royalty amount",
        description: form.errors["moduleSettings.royalty.royaltyAmount"] ? (
          <JumpStepButton
            step={getStepByPath(
              "moduleSettings.royalty.royaltyAmount",
              collectionType
            )}
          >
            {form.errors["moduleSettings.royalty.royaltyAmount"]}
          </JumpStepButton>
        ) : (
          `${moduleSettings.royalty.royaltyAmount}`
        ) /* validation on input should guarantee that royalty decimal precision did not exceed .01 */,
      },
      {
        leftIcon: <PayoutPersonIcon />,
        label: "Royalty payout address",
        description: form.errors["moduleSettings.royalty.address"] ? (
          <JumpStepButton
            step={getStepByPath(
              "moduleSettings.royalty.address",
              collectionType
            )}
          >
            {form.errors["moduleSettings.royalty.address"]}
          </JumpStepButton>
        ) : (
          maskAddress(moduleSettings.royalty.address, 6, 4)
        ),
      }
    );
  }

  if (
    moduleSettings.shouldLimitNumberOfMintPerWallet &&
    distribution.type === TokenDistributionType.Mint
  ) {
    rows.push(
      {
        leftIcon: <LimitMintsIcon />,
        label: "Limit number of mints per wallet",
        description: moduleSettings.shouldLimitNumberOfMintPerWallet
          ? "Yes"
          : "No",
      },
      {
        leftIcon: <MaxMintsIcon />,
        label: "Maximum mints per wallet",
        description: `${moduleSettings.limitPerWallet}`,
      }
    );
  }
  if (moduleSettings.nonTransferable) {
    rows.push({
      leftIcon: <SoulboundIcon />,
      label: "Make this collection non-transferable",
      description: moduleSettings.nonTransferable ? "Yes" : "No",
    });
  }
  rows.push({
    leftIcon: <SponsoredIcon />,
    label: "Sponsorship enabled",
    description: distribution.sponsoredMintsEnabled ? "Yes" : "No",
  });
  if (collection.categories?.length) {
    rows.push({
      leftIcon: <NameIcon />,
      label: "Categories",
      description: collection.categories.join(", "),
    });
  }
  if (collection.tags?.length) {
    rows.push({
      leftIcon: <NameIcon />,
      label: "Tags",
      description: collection.tags.map((tag) => tag.name).join(", "),
    });
  }

  // TODO: for existing contract- check if using CORI
  // https://linear.app/highlight-xyz/issue/ENG-4986/[frontend]-show-cori-filter-setting-for-existing-contracts-on-review
  if (contract.type === "new" && contract.data.usingOpenseaBlocklist) {
    rows.push({
      leftIcon: <OpenseaBlocklistIcon />,
      label: "Add this token to CORI operator filter registry",
      description: contract.data.usingOpenseaBlocklist ? "Yes" : "No",
    });
  }

  const enableSeriesCreation = useFeatureFlags(
    FEATURE_FLAGS.ENABLE_INHOTIM_SERIES
  );
  const skipValidation = useFeatureFlags(
    FEATURE_FLAGS.SKIP_SERIES_ZIP_VALIDATION
  );

  if (!enableSeriesCreation && collectionType === _CollectionType.Series) {
    return <FeatureNotAvailable />;
  }

  const { previewMediaUrl, previewMediaType } = getPreviewImageUrl(form);

  if (buttonOnly) {
    return (
      <>
        {deployModal}
        <DeployFinishTracking txnId={txnId} />

        <Stack>
          <Button
            mt={48}
            size="xl"
            fullWidth
            leftIcon={<DeployIcon />}
            onClick={handleFormSubmit}
            loading={loading}
          >
            {loading ? "Deploying" : "Deploy"} collection
          </Button>
          <Text size="xs" align="center" mt={8}>
            {`Once you initiate the deploy process it can't be paused or
            undone. Make sure all your collection details are correct before
            processing`}
          </Text>
          {generalError && (
            <Text color={STATUS_COLOR.ERROR}>{generalError}</Text>
          )}
        </Stack>
      </>
    );
  }

  return (
    <>
      {deployModal}
      <DeployFinishTracking txnId={txnId} />
      <FormTitle title="Review and deploy" buttons={null} />
      <FormContentWithPreview
        collectionType={collectionType ?? _CollectionType.LimitedEdition}
        mediaUrl={previewMediaUrl}
        mediaType={previewMediaType}
        showDetails
        name={collection.name}
        size={collection.size}
        showPrice={!!distribution.price}
        price={distribution.price}
        currency={displayCurrency}
      >
        {rows.filter(Boolean).map((data, index) => (
          <DetailSectionLine
            key={index}
            {...(data as DetailSectionLineProps)}
          />
        ))}

        <Button
          mt={48}
          size="xl"
          fullWidth
          leftIcon={<DeployIcon />}
          onClick={handleFormSubmit}
          loading={loading}
          disabled={
            !form.isValid() ||
            ([
              _CollectionType.Series,
              _CollectionType.GenerativeSeries,
            ].includes(collectionType) &&
              ([UploadStatus.IN_PROGRESS, UploadStatus.ERROR].includes(
                status
              ) ||
                !state.unzipped))
          }
        >
          {loading ? "Deploying" : "Deploy"} collection
        </Button>
        <Text size="xs" align="center" mt={8}>
          {`Once you initiate the deploy process it can't be paused or
            undone. Make sure all your collection details are correct before
            processing`}
        </Text>
        {generalError && <Text color={STATUS_COLOR.ERROR}>{generalError}</Text>}

        {!skipValidation &&
          [_CollectionType.Series, _CollectionType.GenerativeSeries].includes(
            collectionType
          ) &&
          (status === UploadStatus.IN_PROGRESS ||
            status === UploadStatus.FINISHED) && (
            <>
              <Space h={12} />
              <>
                {status === UploadStatus.FINISHED && !state.unzipped ? (
                  <UnzipProgress />
                ) : status !== UploadStatus.FINISHED ? (
                  <UploadFileProgress />
                ) : (
                  <></>
                )}
              </>
            </>
          )}
      </FormContentWithPreview>
    </>
  );
};

export default memo(ReviewAndDeploy);
